import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Curves not given by y = f(x) — the last unit of Calculus II.
 *
 * Every curve in the course so far has been a graph, and a graph is a very
 * particular kind of curve: one that passes the vertical line test. A circle is
 * not one. Neither is anything that loops, crosses itself, or comes back on
 * itself — which is most of the curves that actually describe something moving.
 *
 * Both lessons therefore open with the same complaint and answer it differently:
 * a parameter lets both coordinates depend on a third variable, and polar
 * coordinates change what a coordinate means. And both derive their calculus
 * from the arc-length triangle rather than stating formulas, because there are
 * four formulas here (slope and arc length, twice over) and no student
 * remembers four formulas they were handed.
 *
 * `parametric-curves` puts the second derivative in the lesson even though it is
 * rarely examined, because the wrong version — dividing the second derivatives —
 * is so nearly plausible that a student who has not been shown it is wrong will
 * invent it. The first derivative *is* a quotient of derivatives, which is
 * exactly why the second one looks as though it should be.
 *
 * `polar-calculus` is built around the factor of one half. The area element is a
 * wedge, not a rectangle, and ∫r dθ — the formula a student writes by analogy
 * with ∫y dx — is not an area at all; it has the wrong units. Every worked
 * example in that lesson checks its answer against a shape whose area is known
 * from geometry, because that is the only check that catches a missing ½.
 */
export const PARAMETRIC_POLAR_LESSONS: Lesson[] = [
  lesson({
    id: 'parametric-curves-01',
    unitId: 'unit-parametric-polar',
    conceptId: 'parametric-curves',
    title: 'Parametric Curves',
    summary: 'Letting x and y both depend on a third variable, so a curve can loop, cross itself and run backwards.',
    objectives: [
      'Eliminate the parameter to recognise a curve, and say what is lost by doing so',
      'Find dy/dx and the second derivative for a parametrically defined curve',
      'Compute the arc length of a parametric curve from the same triangle as before',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Not every curve is a graph',
        blocks: [
          text('A circle of radius 1 is the simplest closed curve there is, and it is not the graph of any function. Writing it as y = ±√(1 − x²) needs two functions, breaks at the sides, and makes both halves undefined at the two points where the curve is perfectly well behaved.'),
          text('The fix is to stop treating one coordinate as the input. Let a third variable drive both:'),
          math('x = \\cos t, \\qquad y = \\sin t, \\qquad 0 \\leq t \\leq 2\\pi'),
          insight('Now nothing is broken. The parameter t is usually time, and the curve is the path of a moving point: at each instant t the point is somewhere, and the curve is the trail it leaves. A path can cross itself, loop, stop and reverse — a graph cannot do any of those things.'),
          text('This also carries *more* information than the curve does. The same circle traced clockwise, traced twice, or traced faster is a different parametrisation of the same set of points, and the difference is invisible in a picture and completely real to anything moving along it.'),
        ],
      },
      intuition: {
        title: 'Slope, curvature and length, from the chain rule',
        blocks: [
          text('**Eliminating the parameter** recovers the underlying curve when you want to identify it. For x = cos t, y = sin t, square and add: x² + y² = 1, a circle. For x = t + 1, y = t², substitute t = x − 1 to get y = (x − 1)².'),
          warning('Elimination loses the direction, the speed and the *range*. x = t², y = t⁴ eliminates to y = x², but the parametric curve is only the right half of the parabola, because t² is never negative. Always check which part of the eliminated curve is actually traced.', 'Elimination throws information away'),
          text('**The slope.** dy/dx still means "how fast y changes per unit of x", and the chain rule delivers it without eliminating anything:'),
          math('\\frac{dy}{dx} = \\frac{dy/dt}{dx/dt} \\qquad \\text{provided } \\frac{dx}{dt} \\neq 0'),
          insight('The t-derivatives are rates with respect to time; their ratio is a rate with respect to x, because the time cancels. This is the one formula in the lesson that looks like what a student would guess — which is precisely the reason the next one catches people.'),
          text('**The second derivative** is *not* the ratio of the second derivatives. It is the derivative of dy/dx with respect to x, and dy/dx is itself a function of t, so the same rule applies again:'),
          math('\\frac{d^{2}y}{dx^{2}} = \\frac{\\dfrac{d}{dt}\\left(\\dfrac{dy}{dx}\\right)}{\\dfrac{dx}{dt}}'),
          warning('Writing (d²y/dt²)/(d²x/dt²) is wrong, and it is wrong in a way that is hard to notice because it is right in shape and dimensionally plausible. Test it on x = t, y = t²: the true second derivative is 2, and the wrong formula gives 2/0. Differentiate the slope, then divide by dx/dt.', 'The second derivative is not a ratio of second derivatives'),
          text('**Arc length** comes from the same triangle as before. Divide ds = √((dx)² + (dy)²) by dt instead of by dx:'),
          math('L = \\int_{\\alpha}^{\\beta}\\sqrt{\\left(\\frac{dx}{dt}\\right)^{2} + \\left(\\frac{dy}{dt}\\right)^{2}}\\;dt'),
          insight('Read that as speed integrated over time, which is exactly what distance travelled is. And note the symmetry the earlier version hid: with a parameter, x and y are treated identically, and the awkward 1 under the root in the y = f(x) version is just (dx/dt)² for the parametrisation x = t.'),
          warning('This computes the distance *travelled*, not the length of the curve drawn — and they differ whenever the parametrisation retraces. Taking t from 0 to 4π on the unit circle gives 4π, which is correct as a distance and twice the circumference.', 'Distance travelled, not length drawn'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'conic', conic: 'circle', a: 1, min: -2, max: 2, label: 'x = cos t, y = sin t' },
            'A curve no function can draw. Every vertical line between −1 and 1 meets it twice, so it fails the vertical line test everywhere; as a parametrised path there is no difficulty at all, because the point is simply somewhere different at each time t.',
          ),
          figure(
            {
              kind: 'coordinate-grid',
              min: -3,
              max: 5,
              points: [
                { x: 0, y: 0, label: 't = 0' },
                { x: 1, y: -2, label: 't = 1' },
                { x: 4, y: 2, label: 't = 2' },
                { x: 1, y: 2, label: 't = −1' },
              ],
            },
            'Four points on x = t², y = t³ − 3t, plotted with their times. The path passes through (1, −2) at t = 1 and through (1, 2) at t = −1 — the same x, two different points, and between them the curve makes a loop. No equation of the form y = f(x) describes this.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For x = t², y = t³ − 3t, find the slope at t = 2, and find the arc length of x = t², y = ⅔t³ from t = 0 to t = 1.'),
          steps([
            { label: 'Differentiate each coordinate with respect to t.', latex: '\\frac{dx}{dt} = 2t, \\qquad \\frac{dy}{dt} = 3t^{2} - 3' },
            { label: 'Their ratio is the slope.', latex: '\\frac{dy}{dx} = \\frac{3t^{2}-3}{2t}' },
            { label: 'Substitute t = 2.', latex: '\\frac{12 - 3}{4} = \\frac{9}{4}' },
          ]),
          text('And the length:'),
          steps([
            { label: 'Differentiate.', latex: '\\frac{dx}{dt} = 2t, \\qquad \\frac{dy}{dt} = 2t^{2}' },
            { label: 'Square, add, and factor out the common 4t².', latex: '(2t)^{2} + (2t^{2})^{2} = 4t^{2}(1 + t^{2})' },
            { label: 'Take the root. On 0 ≤ t ≤ 1, t is positive.', latex: '\\sqrt{4t^{2}(1+t^{2})} = 2t\\sqrt{1+t^{2}}' },
            { label: 'Integrate, substituting u = 1 + t² with limits 1 and 2.', latex: 'L = \\int_{0}^{1} 2t\\sqrt{1+t^{2}}\\,dt = \\int_{1}^{2} u^{1/2}\\,du' },
            { label: 'Evaluate.', latex: 'L = \\left[\\tfrac{2}{3}u^{3/2}\\right]_{1}^{2} = \\tfrac{2}{3}\\big(2\\sqrt{2} - 1\\big) \\approx 1.22' },
          ]),
          insight('The curve runs from (0, 0) to (1, ⅔), a straight-line distance of about 1.20, and the arc is 1.22. Longer than the straight line, and barely — which is right, because over that range the curve hardly bends. The check from the arc-length lesson works unchanged here, because it is a fact about curves rather than about parametrisations.'),
          tip('Taking t out of the square root needs |t|, and it is only equal to t because the interval starts at 0. On an interval running through negative values the integral has to be split, and forgetting that returns a negative "length".'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('parametric-curves', 4)] },
      independent: { title: 'On your own', blocks: [practice('parametric-curves', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('parametric-curves', 3, 'Eliminating the parameter, slopes and second derivatives, and arc lengths built to come out.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A parametrisation gives a path: position as a function of time, not y as a function of x.',
            'Eliminating the parameter loses direction, speed and often part of the curve.',
            'dy/dx = (dy/dt)/(dx/dt), whenever dx/dt is not zero.',
            'The second derivative differentiates the slope with respect to t, then divides by dx/dt.',
            'L = ∫√((dx/dt)² + (dy/dt)²) dt — speed integrated over time.',
            'That is distance travelled, so a curve traced twice has twice the length.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'polar-calculus-01',
    unitId: 'unit-parametric-polar',
    conceptId: 'polar-calculus',
    title: 'Area and Slope in Polar Coordinates',
    summary: 'Calculus on a curve given by a radius and an angle, where the piece you add up is a wedge rather than a strip.',
    objectives: [
      'Sketch a polar curve well enough to know the limits of integration',
      'Find the area enclosed by a polar curve using the wedge element ½r² dθ',
      'Find the slope of a polar curve by treating θ as a parameter',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A curve described by how far out',
        blocks: [
          text('Polar coordinates give a point an address by direction and distance rather than by across and up. A polar *curve* is then a rule saying how far out to go for each direction:'),
          math('r = f(\\theta)'),
          text('and as θ sweeps round, the point at distance f(θ) traces a curve. Some shapes that are painful in Cartesian coordinates become one line here — a circle centred at the origin is r = 3, and a spiral is r = θ.'),
          insight('This is a parametrisation, with θ as the parameter. Everything in the previous lesson applies, and the only new work is translating it into r and θ — which is worth doing once, because the results are the formulas that get used.'),
          math('x = r\\cos\\theta = f(\\theta)\\cos\\theta, \\qquad y = r\\sin\\theta = f(\\theta)\\sin\\theta'),
        ],
      },
      intuition: {
        title: 'The area element is a wedge',
        blocks: [
          text('In Cartesian coordinates, the piece of area you add up is a rectangle: height y, width dx. In polar coordinates it is a thin **wedge** — a sector of a circle of radius r with a tiny angle dθ at the origin.'),
          text('The area of a sector of radius r and angle θ is ½r²θ, so the wedge contributes'),
          math('dA = \\tfrac{1}{2}r^{2}\\,d\\theta'),
          text('and the area swept as θ runs from α to β is'),
          math('A = \\int_{\\alpha}^{\\beta}\\tfrac{1}{2}\\big(f(\\theta)\\big)^{2}\\,d\\theta'),
          warning('∫r dθ is not an area. It has units of length times angle, which is a length, and it is what you get by copying the shape of ∫y dx without asking what the piece looks like. The two things that make the polar version different — the square and the half — both come from the wedge, and both are lost by analogy.', 'The formula is not ∫r dθ'),
          insight('Check the formula against something known before trusting it. On the circle r = a, the area is ½∫a² dθ over a full turn, which is ½a²(2π) = πa². Any polar area answer can be checked this way, and a missing ½ shows up immediately as double a familiar figure.'),
          text('**Getting the limits right** is most of the difficulty, and it needs a sketch rather than a rule. Two questions to ask every time:'),
          list([
            'Over what range of θ is the curve traced exactly once? The circle r = 2cos θ is complete after θ runs from −π/2 to π/2 — going round to 2π retraces it, and doubles the answer.',
            'Where is r = 0? Those angles are where a loop of the curve begins and ends, which makes them the natural limits for the area of one petal.',
          ]),
          text('**The slope.** Treat θ as a parameter and use the parametric rule. Differentiating the two coordinate equations with the product rule:'),
          math('\\frac{dy}{dx} = \\frac{\\dfrac{dr}{d\\theta}\\sin\\theta + r\\cos\\theta}{\\dfrac{dr}{d\\theta}\\cos\\theta - r\\sin\\theta}'),
          warning('dy/dx is not dr/dθ. r is not a height and θ is not a horizontal position, so the ratio of their derivatives is not a slope — it is the rate at which the curve moves away from the origin as it turns, which is a real quantity and a different one.', 'dr/dθ is not a slope'),
          text('**Arc length**, for completeness, comes from the same triangle once more:'),
          math('L = \\int_{\\alpha}^{\\beta}\\sqrt{r^{2} + \\left(\\frac{dr}{d\\theta}\\right)^{2}}\\;d\\theta'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'circle-sector', degrees: 30, radiusLabel: 'r', arcLabel: 'r dθ', label: 'one wedge' },
            'The piece being added up. A thin sector of radius r and angle dθ has area ½r² dθ — nearly a triangle with base r dθ and height r. Both the square and the half come from this picture, and neither can be guessed from the Cartesian formula.',
          ),
          figure(
            { kind: 'unit-circle', angle: 60, showCoordinates: true, marks: [0, 90, 180, 270] },
            'How a polar curve is traced. As θ increases the point swings anticlockwise, and r says how far out along that ray to place it. Knowing where the ray has come from and where it is going is what settles the limits of an area integral — which is why polar area starts with a sketch, not with a formula.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the area enclosed by r = 2cos θ.'),
          steps([
            { label: 'Identify the curve. Multiplying by r gives r² = 2r cos θ.', latex: 'x^{2} + y^{2} = 2x \\quad\\Longrightarrow\\quad (x-1)^{2} + y^{2} = 1', note: 'A circle of radius 1 centred at (1, 0) — so the answer must come out as π.' },
            { label: 'Find the range of θ that traces it once. r = 0 at θ = ±π/2.', latex: '-\\frac{\\pi}{2} \\leq \\theta \\leq \\frac{\\pi}{2}' },
            { label: 'Set up the wedge integral.', latex: 'A = \\frac{1}{2}\\int_{-\\pi/2}^{\\pi/2}\\big(2\\cos\\theta\\big)^{2}\\,d\\theta = 2\\int_{-\\pi/2}^{\\pi/2}\\cos^{2}\\theta\\,d\\theta' },
            { label: 'An even power — use the half-angle identity.', latex: '= 2\\int_{-\\pi/2}^{\\pi/2}\\frac{1 + \\cos 2\\theta}{2}\\,d\\theta' },
            { label: 'The cos 2θ term integrates to sin 2θ/2, which vanishes at both ends.', latex: '= \\left[\\theta + \\tfrac{1}{2}\\sin 2\\theta\\right]_{-\\pi/2}^{\\pi/2}' },
            { label: 'Evaluate.', latex: 'A = \\pi' },
          ]),
          insight('The answer matched the geometry, which is the only reason to trust it. Had the limits run from 0 to 2π the integral would have returned 2π — the circle traced twice, its area counted twice — and nothing in the calculation would have looked wrong. Sketch first; the algebra cannot tell you how many times you went round.'),
          tip('A quick unit check catches the other classic error. Area must scale as a length squared, so doubling r must quadruple the answer. ½∫r² dθ does; ∫r dθ does not, and that alone is enough to rule it out without remembering which formula was correct.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('polar-calculus', 4)] },
      independent: { title: 'On your own', blocks: [practice('polar-calculus', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('polar-calculus', 3, 'Wedge areas, petals of a rose, limits that must be found from r = 0, and slopes in polar form.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A polar curve r = f(θ) is a parametrisation with θ as the parameter.',
            'The area element is a wedge: dA = ½r² dθ.',
            '∫r dθ is not an area — it does not even have the units of one.',
            'Find the limits from a sketch and from where r = 0, or you will trace the curve twice.',
            'Slope: use the parametric rule on x = r cos θ, y = r sin θ. dr/dθ is not a slope.',
            'Check every answer against a shape whose area you already know.',
          ]),
        ],
      },
    },
  }),
];
